import React, {useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ReactComponent as SettingsIcon} from 'assets/svg/options.svg';
import {Page} from 'components/common/Page';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {
    blockEmployeeFx,
    Gate,
    personnelNkState$,
    unblockEmployeeFx,
} from 'models/profile/personnelNk';
import {AppealTheme, NkEmployee} from 'types';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {
    ActionsContainer,
    Action,
    CreateUserButton,
    HeaderControls,
    UserStatusTag,
} from '../Staff/styled';
import {ConfirmBlockMessage, ConfirmRemoveFooter} from '../Staff/UpdateUser/styled';
import {ListCellWrapper, Wrapper} from '../styled';

import {CellContent} from './styled';

enum PersonnelNkAccessor {
    name = 'name',
    email = 'email',
    phone = 'phone',
    role = 'role',
    branches = 'branches',
    themes = 'themes',
    status = 'status',
    actions = 'actions',
}

const getFullName = ({firstName, middleName, lastName}: NkEmployee) =>
    [firstName, middleName, lastName].filter(Boolean).join(' ');

const getColumns = (t: TFunction) =>
    mapTableColumns([
        {id: PersonnelNkAccessor.name, header: t('pages.profile.personnelNk.name'), width: 170},
        {id: PersonnelNkAccessor.email, header: t('pages.profile.personnelNk.email'), width: 200},
        {id: PersonnelNkAccessor.phone, header: t('pages.profile.personnelNk.phone'), width: 125},
        {id: PersonnelNkAccessor.role, header: t('pages.profile.personnelNk.role'), width: 120},
        {
            id: PersonnelNkAccessor.branches,
            header: t('pages.profile.personnelNk.branches'),
            width: 180,
        },
        {
            id: PersonnelNkAccessor.themes,
            header: t('pages.profile.personnelNk.themes'),
            width: 190,
        },
        {id: PersonnelNkAccessor.status, header: t('pages.profile.personnelNk.status'), width: 90},
        {
            id: PersonnelNkAccessor.actions,
            header: t('pages.profile.personnelNk.actions'),
            width: 130,
        },
    ]);

const mapEmployees = (
    employees: NkEmployee[],
    themes: AppealTheme[],
    t: TFunction,
    onChangeStatus: (employee: NkEmployee) => void,
): Array<Record<PersonnelNkAccessor, React.ReactNode>> => {
    const themeNames = new Map(themes.map((theme) => [theme.code, theme.name]));

    return employees.map((employee) => ({
        [PersonnelNkAccessor.name]: handleNil(getFullName(employee), (value: string) => (
            <CellContent>{value}</CellContent>
        )),
        [PersonnelNkAccessor.email]: handleNil(employee.email, (value: string) => (
            <CellContent>{value}</CellContent>
        )),
        [PersonnelNkAccessor.phone]: handleNil(employee.phone, (value: string) => (
            <CellContent>{value}</CellContent>
        )),
        [PersonnelNkAccessor.role]: employee.role
            ? t(`pages.profile.personnelNk.roles.${employee.role}`)
            : '—',
        [PersonnelNkAccessor.branches]: handleNil(
            employee.branches?.length ? employee.branches : null,
            (branches: NkEmployee['branches']) => (
                <ListCellWrapper>
                    {branches?.map((branch) => (
                        <li key={branch.id}>{branch.name}</li>
                    ))}
                </ListCellWrapper>
            ),
        ),
        [PersonnelNkAccessor.themes]: handleNil(
            employee.themeCodes?.length ? employee.themeCodes : null,
            (themeCodes: string[]) => (
                <ListCellWrapper>
                    {themeCodes.map((code) => (
                        <li key={code}>{themeNames.get(code) || code}</li>
                    ))}
                </ListCellWrapper>
            ),
        ),
        [PersonnelNkAccessor.status]: (
            <UserStatusTag isActive={!employee.isBlocked}>
                {t(
                    `pages.profile.personnelNk.statuses.${
                        employee.isBlocked ? 'BLOCKED' : 'ACTIVE'
                    }`,
                )}
            </UserStatusTag>
        ),
        [PersonnelNkAccessor.actions]: (
            <ActionsContainer>
                <Action
                    title={t('pages.profile.personnelNk.edit')}
                    onClick={() =>
                        history.push(generatePath(ROUTE.PERSONNEL_NK_UPDATE, {id: employee.userId}))
                    }
                >
                    <Icon source={SettingsIcon} fillColor="#000" width={18} height={18} />
                </Action>
                <Action onClick={() => onChangeStatus(employee)}>
                    {t(`pages.profile.personnelNk.${employee.isBlocked ? 'unblock' : 'block'}`)}
                </Action>
            </ActionsContainer>
        ),
    }));
};

export const PersonnelNk = () => {
    const {t} = useTranslation();
    const state = useStore(personnelNkState$);
    const blockPending = useStore(blockEmployeeFx.pending);
    const unblockPending = useStore(unblockEmployeeFx.pending);
    const [employeeToChangeStatus, setEmployeeToChangeStatus] = useState<NkEmployee | null>(null);
    const [reason, setReason] = useState('');
    const [showReason, setShowReason] = useState(false);
    useGate(Gate);

    const columns = useMemo(() => getColumns(t), [t]);
    const data = useMemo(
        () => mapEmployees(state.employees, state.themes, t, setEmployeeToChangeStatus),
        [state.employees, state.themes, t],
    );
    const headerControls = (
        <HeaderControls>
            <CreateUserButton size="sm" onClick={() => history.push(ROUTE.PERSONNEL_NK_CREATE)}>
                + {t('pages.profile.personnelNk.create')}
            </CreateUserButton>
        </HeaderControls>
    );
    const closeStatusModal = () => {
        setEmployeeToChangeStatus(null);
        setReason('');
        setShowReason(false);
    };
    const changeEmployeeStatus = async () => {
        if (!employeeToChangeStatus || !reason.trim()) return;

        const params = {id: employeeToChangeStatus.userId, reason: reason.trim()};
        try {
            if (employeeToChangeStatus.isBlocked) await unblockEmployeeFx(params);
            else await blockEmployeeFx(params);
            closeStatusModal();
        } catch {
            // The effect watcher displays the API error and keeps the modal open.
        }
    };

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <Table
                        columns={columns}
                        data={data}
                        totalRowsCount={data.length}
                        isLoading={state.isLoading}
                        headerControls={headerControls}
                        hideSetting
                        hasFooter={false}
                    />
                </ContentContainer>
            </Wrapper>
            <Modal
                title={t(
                    `pages.profile.personnelNk.confirmStatus.${
                        employeeToChangeStatus?.isBlocked ? 'unblockTitle' : 'blockTitle'
                    }`,
                )}
                modalWidth={0}
                show={!!employeeToChangeStatus}
                onClose={closeStatusModal}
                closable={!blockPending && !unblockPending}
            >
                {employeeToChangeStatus && (
                    <>
                        <ConfirmBlockMessage>
                            {t(
                                `pages.profile.personnelNk.confirmStatus.${
                                    employeeToChangeStatus.isBlocked
                                        ? 'unblockMessage'
                                        : 'blockMessage'
                                }`,
                            )}
                        </ConfirmBlockMessage>
                        {showReason && (
                            <Input
                                isRequired
                                label={t('pages.profile.personnelNk.confirmStatus.reasonLabel')}
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                                placeholder={t('pages.profile.personnelNk.confirmStatus.reason')}
                                style={{marginBottom: 10}}
                            />
                        )}
                        <ConfirmRemoveFooter>
                            <Button
                                type="button"
                                variant={ButtonVariant.Outline}
                                disabled={blockPending || unblockPending}
                                onClick={closeStatusModal}
                            >
                                {t('pages.profile.personnelNk.form.cancel')}
                            </Button>
                            <Button
                                type="button"
                                onClick={() =>
                                    showReason ? changeEmployeeStatus() : setShowReason(true)
                                }
                                disabled={
                                    blockPending || unblockPending || (showReason && !reason.trim())
                                }
                                isLoading={
                                    employeeToChangeStatus.isBlocked ? unblockPending : blockPending
                                }
                            >
                                {t(
                                    `pages.profile.personnelNk.confirmStatus.${
                                        showReason
                                            ? employeeToChangeStatus.isBlocked
                                                ? 'unblock'
                                                : 'block'
                                            : 'confirm'
                                    }`,
                                )}
                            </Button>
                        </ConfirmRemoveFooter>
                    </>
                )}
            </Modal>
        </Page>
    );
};
